import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { useWidgetPageDeleteApi } from '@/common/api/modules/widgetPage'
import {
  CreateWidgetPageResponse,
  CreateWidgetPageVars,
  UpdateWidgetPageResponse,
  UpdateWidgetPageVars
} from '@/common/api/params/widgetPage'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { widgetPageEditPath, widgetPageListPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'
import { WidgetPage } from '@/common/model/recommend/widgetPage'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  AI_INSTRUCTIONS_MAX,
  WidgetPageFormSchemaType,
  convertWidgetPageSchemaToVars,
  convertWidgetPageToSchema,
  makeDefaultWidgetPageSchema,
  widgetPageNameLabelMapping,
  widgetPageSchema
} from '@/components/container/form/modules/recommend/widgetPage/widgetPageSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextArea from '@/components/container/form/widgets/FormTextArea'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'

interface Props {
  widgetPage?: WidgetPage
  onRefetch?(): void
}

const WidgetPageForm = ({ widgetPage, onRefetch }: Props) => {
  const navigate = useNavigate()
  const siteId = useSiteId()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<WidgetPageFormSchemaType>({
    resolver: zodResolver(widgetPageSchema),
    values: widgetPage
      ? convertWidgetPageToSchema(widgetPage)
      : makeDefaultWidgetPageSchema(siteId),
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, widgetPageNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!widgetPage })

  const { trigger } = useCallMutation<
    CreateWidgetPageResponse | UpdateWidgetPageResponse,
    CreateWidgetPageVars | UpdateWidgetPageVars
  >(
    widgetPage ? ApiPath.UPDATE_WIDGET_PAGE : ApiPath.CREATE_WIDGET_PAGE,
    {
      onSuccess: (response) => {
        showSaved(response.name)
        widgetPage && onRefetch ? onRefetch() : navigate(widgetPageEditPath(siteId, response.id))
      }
    },
    widgetPage ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<WidgetPageFormSchemaType> = (data) =>
    trigger(convertWidgetPageSchemaToVars(data))

  // 削除（編集時のみ）
  const { onClickDelete } = useWidgetPageDeleteApi(widgetPage ?? ({} as WidgetPage), () =>
    navigate(widgetPageListPath(siteId))
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={widgetPage ? widgetPage.name : 'レコメンドパーツ設置ページを追加する'}
        badgeNode={<AppTitleBadge type='recommend' />}
      />
      {isDirty && (
        <TopBar
          status={widgetPage ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={widgetPage ? '未反映の変更があります' : '登録されていない新しい設置ページ'}
          onCancel={() =>
            reset(
              widgetPage
                ? convertWidgetPageToSchema(widgetPage)
                : makeDefaultWidgetPageSchema(siteId)
            )
          }
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本設定</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel required id='name'>
                レコメンドパーツ設置ページ名
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('name')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.name?.message}
                  size={TextBoxSize.FULL}
                  placeholder='TOPページ'
                />
                <FormError message={errors.name?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel required id='targetUrl'>
                ページURL
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('targetUrl')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.targetUrl?.message}
                  size={TextBoxSize.FULL}
                  placeholder='https://www.example.com/'
                />
                <FormError message={errors.targetUrl?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='aiInstructions'>デザイン指示</FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextArea
                  register={register('aiInstructions')}
                  control={control}
                  name='aiInstructions'
                  error={!!errors.aiInstructions?.message}
                  counter
                  maxLength={AI_INSTRUCTIONS_MAX}
                  placeholder='例）サイトのテイストに合うように緑と青を基調としてください。フォントサイズは12pxを下回らないようにしてください。'
                />
                <FormError message={errors.aiInstructions?.message} />
                <FormCaption>
                  ページ全体に反映すべきデザイン指示を入力してください。パーツ個別の指示は、カスタムCSS生成時に指定できます。
                </FormCaption>
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='basicAuthUser'>Basic認証</FormLabel>
              <FormRow direction={'vertical'} gap={8}>
                <FormRow gap={8}>
                  <FormTextField
                    register={register('basicAuthUser')}
                    error={!!errors.basicAuthUser?.message}
                    size={TextBoxSize.FULL}
                    label={'ユーザー名'}
                  />
                  <FormTextField
                    register={register('basicAuthPass')}
                    error={!!errors.basicAuthPass?.message}
                    size={TextBoxSize.FULL}
                    type={FormTextFieldType.PASSWORD}
                    label={'パスワード'}
                    placeholder={
                      widgetPage?.has_basic_auth_pass
                        ? '変更しない場合は空のままにしてください'
                        : ''
                    }
                  />
                </FormRow>
                <FormError message={errors.basicAuthUser?.message} />
                <FormError message={errors.basicAuthPass?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom between={!!widgetPage}>
        {widgetPage && (
          <Button color={ButtonColor.CRITICAL} size='large' onClick={onClickDelete}>
            この設定を削除する
          </Button>
        )}
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {widgetPage ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default WidgetPageForm
